import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Toaster } from './ui/toaster';
import OpticalTransistorOperation from './OpticalTransistorAnimation';
import { 
  Shield, 
  Cpu, 
  Network, 
  TrendingUp, 
  Zap, 
  Leaf, 
  BarChart3, 
  ChevronRight, 
  Mail, 
  MessageCircle,
  CheckCircle,
  ArrowRight,
  Building,
  Flag,
  Target,
  Eye,
  Atom,
  Star,
  Award,
  Globe,
  Lock,
  Send,
  User,
  Phone,
  Settings
} from 'lucide-react';
import mockData from '../mock';

const OpticalDTLanding = () => {
  const [activePhase, setActivePhase] = useState(1);
  const [scrollY, setScrollY] = useState(0);
  const [isPartnershipModalOpen, setIsPartnershipModalOpen] = useState(false);
  const [isTechModalOpen, setIsTechModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isGovModalOpen, setIsGovModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    inquiryType: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e, formType) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission - in real app, this would be an API call
      const emailSubject = `OpticalDT ${formType} Inquiry - ${formData.name}`;
      const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company}
Phone: ${formData.phone}
Inquiry Type: ${formData.inquiryType}
Message: ${formData.message}
Form Type: ${formType}
      `;

      // Create mailto link
      const mailtoLink = `mailto:bombela@softdab.tech?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      window.open(mailtoLink, '_self');

      toast({
        title: "Inquiry Sent Successfully!",
        description: "Your email client has been opened. Please send the email to complete your inquiry.",
      });

      // Reset form
      setFormData({ name: '', email: '', company: '', phone: '', inquiryType: '', message: '' });
      setIsPartnershipModalOpen(false);
      setIsTechModalOpen(false);
      setIsContactModalOpen(false);
      setIsGovModalOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an issue sending your inquiry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const ContactForm = ({ formType, title, description }) => (
    <form onSubmit={(e) => handleFormSubmit(e, formType)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            className="mt-1"
            placeholder="Your full name"
          />
        </div>
        <div>
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
            className="mt-1"
            placeholder="your@email.com"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="company">Organization/Company</Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            className="mt-1"
            placeholder="Your organization"
          />
        </div>
        <div>
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="mt-1"
            placeholder="+1 (555) 123-4567"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="inquiryType">Inquiry Type</Label>
        <Select value={formData.inquiryType} onValueChange={(value) => handleInputChange('inquiryType', value)}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select inquiry type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="partnership">Strategic Partnership</SelectItem>
            <SelectItem value="investment">Investment Opportunity</SelectItem>
            <SelectItem value="technology">Technology Demo</SelectItem>
            <SelectItem value="government">Government Program</SelectItem>
            <SelectItem value="general">General Inquiry</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          value={formData.message}
          onChange={(e) => handleInputChange('message', e.target.value)}
          required
          className="mt-1 min-h-[100px]"
          placeholder="Please describe your inquiry and how we can help..."
        />
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (
          <>Sending...</>
        ) : (
          <>
            <Send className="mr-2 w-4 h-4" />
            Send Inquiry
          </>
        )}
      </Button>
    </form>
  );

  const iconMap = {
    Shield,
    Cpu,
    Network,
    TrendingUp,
    Zap,
    Leaf,
    BarChart3,
    Atom,
    Star,
    Award,
    Globe,
    Lock,
    Send,
    User,
    Phone
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-slate-200 z-50">
        <div className="container mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Zap className="w-4 h-4 md:w-5 md:h-5 text-white" />
              </div>
              <span className="text-lg md:text-xl font-bold text-slate-900">OpticalDT</span>
            </div>
            <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <a href="#about" className="text-slate-600 hover:text-blue-600 transition-colors text-sm lg:text-base">About</a>
              <a href="#technology" className="text-slate-600 hover:text-blue-600 transition-colors text-sm lg:text-base">Technology</a>
              <a href="#roadmap" className="text-slate-600 hover:text-blue-600 transition-colors text-sm lg:text-base">Roadmap</a>
              <a href="#contact" className="text-slate-600 hover:text-blue-600 transition-colors text-sm lg:text-base">Contact</a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-16 md:pt-20 pb-12 md:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-indigo-600/5" />
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
            background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)'
          }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 md:mb-6 leading-tight px-2">
              {mockData.hero.title}
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 mb-8 md:mb-12 leading-relaxed px-2">
              {mockData.hero.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-12 md:mb-16 px-4">
              <Dialog open={isPartnershipModalOpen} onOpenChange={setIsPartnershipModalOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg">
                    Partner with Us
                    <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Building className="w-5 h-5 text-blue-600" />
                      Strategic Partnership Inquiry
                    </DialogTitle>
                    <DialogDescription>
                      Join Intel, government agencies, and strategic partners in revolutionizing computing through optical technology.
                    </DialogDescription>
                  </DialogHeader>
                  <ContactForm 
                    formType="Strategic Partnership" 
                    title="Partnership Inquiry"
                    description="Explore collaboration opportunities"
                  />
                </DialogContent>
              </Dialog>

              <Dialog open={isTechModalOpen} onOpenChange={setIsTechModalOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" variant="outline" className="border-2 border-blue-200 text-blue-700 hover:bg-blue-50 px-6 md:px-8 py-4 md:py-6 text-base md:text-lg">
                    View Technology
                    <ChevronRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-blue-600" />
                      Technology Demonstration Request
                    </DialogTitle>
                    <DialogDescription>
                      Schedule a technical demonstration of OpticalDT's breakthrough optical processor technology.
                    </DialogDescription>
                  </DialogHeader>
                  <ContactForm 
                    formType="Technology Demo" 
                    title="Technology Demo"
                    description="Request technical demonstration"
                  />
                </DialogContent>
              </Dialog>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8 max-w-3xl mx-auto px-4">
              {mockData.hero.stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-blue-600 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-slate-600 font-medium text-sm md:text-base">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {mockData.aboutUs.title}
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                {mockData.aboutUs.subtitle}
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-slate-700 mb-8 leading-relaxed">
                  {mockData.aboutUs.content}
                </p>
                
                <div className="space-y-4">
                  {mockData.aboutUs.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-6">
                <Card className="text-center p-6 border-2 border-blue-100 hover:border-blue-200 transition-colors">
                  <Target className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Our Mission</h3>
                  <p className="text-sm text-slate-600">Enable ultra-fast, energy-efficient computing</p>
                </Card>
                
                <Card className="text-center p-6 border-2 border-indigo-100 hover:border-indigo-200 transition-colors">
                  <Eye className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">Our Vision</h3>
                  <p className="text-sm text-slate-600">Lead optical computing revolution</p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 md:mb-4">
                {mockData.technology.title}
              </h2>
              <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-6 md:mb-8 px-2">
                {mockData.technology.subtitle}
              </p>
              <p className="text-base md:text-lg text-slate-700 max-w-4xl mx-auto px-2">
                {mockData.technology.description}
              </p>
            </div>

            {/* TTL Compatibility Section */}
            <div className="mb-16 md:mb-20">
              <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                <CardHeader className="text-center pb-6">
                  <CardTitle className="text-2xl md:text-3xl text-green-900 mb-4">
                    {mockData.technology.ttlCompatibility.title}
                  </CardTitle>
                  <CardDescription className="text-lg text-green-700 mb-4">
                    {mockData.technology.ttlCompatibility.subtitle}
                  </CardDescription>
                  <p className="text-base md:text-lg text-slate-700 max-w-4xl mx-auto leading-relaxed">
                    {mockData.technology.ttlCompatibility.description}
                  </p>
                </CardHeader>
                
                <CardContent className="p-6 md:p-8">
                  {/* TTL Benefits Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {mockData.technology.ttlCompatibility.benefits.map((benefit, index) => {
                      const iconMap = { Cpu, Settings: Settings, Tool: Settings, Shield };
                      const IconComponent = iconMap[benefit.icon] || Settings;
                      
                      return (
                        <Card key={index} className="p-4 bg-white border-2 border-green-200 hover:border-green-300 transition-all duration-300 hover:shadow-md">
                          <CardHeader className="text-center pb-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-3">
                              <IconComponent className="w-6 h-6 text-white" />
                            </div>
                            <CardTitle className="text-base font-semibold text-slate-900 mb-2">
                              {benefit.title}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <p className="text-sm text-slate-700 text-center leading-relaxed">
                              {benefit.description}
                            </p>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Key Advantages List */}
                  <div className="bg-white rounded-lg p-6 border-2 border-green-200">
                    <h4 className="text-lg font-semibold text-green-900 mb-4 text-center">
                      Seamless Intel Integration Advantages
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {mockData.technology.ttlCompatibility.keyAdvantages.map((advantage, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700 text-sm leading-relaxed">{advantage}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Step-by-Step Optical Transistor Operation */}
            <div className="mb-16 md:mb-20">
              <OpticalTransistorOperation />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {mockData.technology.features.map((feature, index) => (
                <Card key={index} className="p-6 md:p-8 border-2 border-slate-200 hover:border-blue-200 transition-all duration-300 hover:shadow-lg">
                  <CardHeader className="text-center pb-4 md:pb-6">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                      {index === 0 && <Zap className="w-7 h-7 md:w-8 md:h-8 text-white" />}
                      {index === 1 && <Leaf className="w-7 h-7 md:w-8 md:h-8 text-white" />}
                      {index === 2 && <BarChart3 className="w-7 h-7 md:w-8 md:h-8 text-white" />}
                    </div>
                    <CardTitle className="text-lg md:text-xl mb-2">{feature.title}</CardTitle>
                    <CardDescription className="text-sm md:text-base">{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 md:space-y-3">
                      {feature.benefits.map((benefit, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700 text-sm md:text-base">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Value Proposition Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {mockData.valueProposition.title}
              </h2>
              <p className="text-xl text-slate-600 mb-6">
                {mockData.valueProposition.subtitle}
              </p>
              <p className="text-lg text-slate-700 max-w-4xl mx-auto">
                {mockData.valueProposition.description}
              </p>
            </div>

            {/* Intel & Government Benefits */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
              {/* Intel Benefits */}
              <Card className="p-6 md:p-8 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
                <CardHeader className="pb-4 md:pb-6">
                  <div className="flex items-center space-x-3 mb-3 md:mb-4">
                    <Building className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
                    <CardTitle className="text-xl md:text-2xl text-blue-900">{mockData.valueProposition.intel.title}</CardTitle>
                  </div>
                  <CardDescription className="text-sm md:text-base text-slate-700 leading-relaxed">
                    {mockData.valueProposition.intel.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 md:space-y-4">
                    {mockData.valueProposition.intel.benefits.map((benefit, index) => (
                      <div key={index} className="border-l-4 border-blue-300 pl-3 md:pl-4">
                        <h4 className="font-semibold text-slate-900 mb-1 text-sm md:text-base">{benefit.title}</h4>
                        <p className="text-xs md:text-sm text-slate-700 mb-1">{benefit.description}</p>
                        <Badge className="bg-blue-100 text-blue-800 text-xs">{benefit.impact}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              {/* Government Opportunities */}
              <Card className="p-6 md:p-8 border-2 border-red-200 bg-gradient-to-br from-red-50 to-white">
                <CardHeader className="pb-4 md:pb-6">
                  <div className="flex items-center space-x-3 mb-3 md:mb-4">
                    <Flag className="w-6 h-6 md:w-8 md:h-8 text-red-600" />
                    <CardTitle className="text-xl md:text-2xl text-red-900">{mockData.valueProposition.government.title}</CardTitle>
                  </div>
                  <CardDescription className="text-sm md:text-base text-slate-700 leading-relaxed">
                    {mockData.valueProposition.government.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 md:space-y-4">
                    {mockData.valueProposition.government.opportunities.map((opp, index) => (
                      <div key={index} className="border-l-4 border-red-300 pl-3 md:pl-4">
                        <h4 className="font-semibold text-slate-900 mb-1 text-sm md:text-base">{opp.title}</h4>
                        <p className="text-xs md:text-sm text-slate-700 mb-2">{opp.description}</p>
                        {opp.agencies && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {opp.agencies.map((agency, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{agency}</Badge>
                            ))}
                          </div>
                        )}
                        {opp.applications && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {opp.applications.map((app, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{app}</Badge>
                            ))}
                          </div>
                        )}
                        {opp.funding && (
                          <div className="flex flex-wrap gap-1 mb-1">
                            {opp.funding.map((fund, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">{fund}</Badge>
                            ))}
                          </div>
                        )}
                        <Badge className="bg-red-100 text-red-800 text-xs">{opp.impact}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Strategic Call to Action */}
            <div className="mt-16 text-center">
              <Card className="p-8 bg-gradient-to-r from-slate-900 to-blue-900 text-white border-0">
                <CardHeader className="pb-6">
                  <CardTitle className="text-2xl md:text-3xl font-bold mb-4">
                    {mockData.valueProposition.callToAction.title}
                  </CardTitle>
                  <CardDescription className="text-lg text-blue-100 mb-4">
                    {mockData.valueProposition.callToAction.description}
                  </CardDescription>
                  <Badge className="bg-red-600 text-white text-sm px-4 py-2">
                    ⚡ {mockData.valueProposition.callToAction.urgency}
                  </Badge>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {mockData.valueProposition.callToAction.invitations.map((invitation, index) => (
                      <Card key={index} className="p-4 bg-white/10 backdrop-blur-md border-white/20 text-center hover:bg-white/20 transition-colors">
                        <p className="text-white font-medium text-sm">{invitation}</p>
                      </Card>
                    ))}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                    <Dialog open={isPartnershipModalOpen} onOpenChange={setIsPartnershipModalOpen}>
                      <DialogTrigger asChild>
                        <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-6 md:px-8 py-3 md:py-4 text-sm md:text-base">
                          <Building className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                          Partner with Us
                        </Button>
                      </DialogTrigger>
                    </Dialog>
                    
                    <Dialog open={isGovModalOpen} onOpenChange={setIsGovModalOpen}>
                      <DialogTrigger asChild>
                        <Button size="lg" variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-6 md:px-8 py-3 md:py-4 text-sm md:text-base">
                          <Flag className="mr-2 w-4 h-4 md:w-5 md:h-5" />
                          Government Programs
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Flag className="w-5 h-5 text-red-600" />
                            Government Program Inquiry
                          </DialogTitle>
                          <DialogDescription>
                            Explore opportunities for government partnerships, defense contracts, and national security applications.
                          </DialogDescription>
                        </DialogHeader>
                        <ContactForm 
                          formType="Government Program" 
                          title="Government Program"
                          description="Government partnership inquiry"
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {mockData.partnership.title}
              </h2>
              <p className="text-xl text-slate-600">
                {mockData.partnership.subtitle}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {mockData.partnership.advantages.map((advantage, index) => {
                const IconComponent = iconMap[advantage.icon];
                return (
                  <Card key={index} className="p-6 text-center border-2 border-slate-200 hover:border-blue-200 transition-all duration-300 hover:shadow-lg">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{advantage.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{advantage.description}</p>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-slate-900 mb-3 md:mb-4">
                {mockData.roadmap.title}
              </h2>
              <p className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8 px-2">
                {mockData.roadmap.subtitle}
              </p>
            </div>
            
            {/* Timeline */}
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 md:transform md:-translate-x-0.5 w-0.5 md:w-1 h-full bg-gradient-to-b from-blue-200 via-blue-300 to-blue-400"></div>
              
              <div className="space-y-8 md:space-y-12">
                {mockData.roadmap.phases.map((phase, index) => (
                  <div key={phase.id} className={`relative ${index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'} pl-12 md:pl-0`}>
                    {/* Timeline dot */}
                    <div 
                      className={`absolute top-6 w-4 h-4 md:w-6 md:h-6 rounded-full border-2 md:border-4 border-white cursor-pointer transition-all duration-300 ${
                        activePhase === phase.id 
                          ? 'bg-blue-600 shadow-lg scale-125' 
                          : phase.status === 'ready' 
                            ? 'bg-green-500' 
                            : 'bg-slate-300'
                      }`}
                      style={{ 
                        left: '1rem',
                        [index % 2 === 0 ? 'right' : 'left']: window.innerWidth >= 768 ? '50%' : '1rem',
                        transform: window.innerWidth >= 768 ? (index % 2 === 0 ? 'translateX(50%)' : 'translateX(-50%)') : 'none'
                      }}
                      onClick={() => setActivePhase(phase.id)}
                    />
                    
                    <Card 
                      className={`p-4 md:p-6 cursor-pointer transition-all duration-300 border-2 ${
                        activePhase === phase.id 
                          ? 'border-blue-300 shadow-lg bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      } ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}
                      onClick={() => setActivePhase(phase.id)}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 md:mb-4">
                        <Badge variant={phase.status === 'ready' ? 'default' : 'secondary'} className="mb-2 self-start text-xs">
                          Phase {phase.id}
                        </Badge>
                        <span className="text-xs md:text-sm text-slate-500 font-medium">{phase.timeline}</span>
                      </div>
                      
                      <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2">{phase.title}</h3>
                      <p className="text-slate-600 mb-4 text-sm md:text-base">{phase.description}</p>
                      
                      {activePhase === phase.id && (
                        <div className="mt-4 md:mt-6 space-y-3 md:space-y-4 animate-in fade-in duration-300">
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2 text-sm md:text-base">Key Activities:</h4>
                            <ul className="space-y-1 md:space-y-2">
                              {phase.activities.map((activity, idx) => (
                                <li key={idx} className="flex items-start space-x-2 text-xs md:text-sm">
                                  <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-slate-700">{activity}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          <Separator />
                          
                          <div>
                            <h4 className="font-semibold text-slate-900 mb-2 text-sm md:text-base">Expected Outcome:</h4>
                            <p className="text-xs md:text-sm text-slate-700 italic">{phase.outcome}</p>
                          </div>
                        </div>
                      )}
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {mockData.contact.title}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {mockData.contact.subtitle}
            </p>
            <p className="text-lg text-slate-300 mb-12">
              {mockData.contact.description}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {mockData.contact.callToActions.map((action, index) => (
                <Card key={index} className="p-6 bg-white/10 backdrop-blur-md border-white/20 text-center hover:bg-white/20 transition-colors">
                  <p className="text-white font-medium">{action}</p>
                </Card>
              ))}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Dialog open={isContactModalOpen} onOpenChange={setIsContactModalOpen}>
                <DialogTrigger asChild>
                  <Button 
                    size="lg" 
                    className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg"
                  >
                    <Mail className="mr-2 w-5 h-5" />
                    Email Partnership
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-blue-600" />
                      Contact OpticalDT
                    </DialogTitle>
                    <DialogDescription>
                      Get in touch with our team for partnerships, investments, or general inquiries.
                    </DialogDescription>
                  </DialogHeader>
                  <ContactForm 
                    formType="General Contact" 
                    title="Contact Form"
                    description="General inquiry form"
                  />
                </DialogContent>
              </Dialog>
              
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => window.open(`https://wa.me/${mockData.contact.whatsapp.replace(/[\s+()-]/g, '')}?text=Hello, I'm interested in OpticalDT's partnership opportunities.`, '_blank')}
              >
                <MessageCircle className="mr-2 w-5 h-5" />
                WhatsApp Contact
              </Button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-8 justify-center text-sm text-slate-300">
              <div className="flex items-center justify-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>{mockData.contact.email}</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <MessageCircle className="w-4 h-4" />
                <span>{mockData.contact.whatsapp}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-slate-400 border-t border-slate-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-white">OpticalDT</span>
            </div>
            <p className="text-sm text-center">
              © 2025 OpticalDT. Revolutionizing Computing Through Optical Technology.
            </p>
          </div>
        </div>
      </footer>
      <Toaster />
    </div>
  );
};

export default OpticalDTLanding;